import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import AIBusinessPlan from "@/components/AIBusinessPlan";
import { Sparkles, Rocket } from "lucide-react";
import { trackBusinessPlanCompleted } from "@/utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AIBusinessPlanPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { studentId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [canGenerate, setCanGenerate] = useState(false);
  const [completedSessions, setCompletedSessions] = useState(0);
  const [remainingRegenerations, setRemainingRegenerations] = useState(3);

  useEffect(() => {
    loadAIPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const loadAIPlan = async () => {
    try {
      setLoading(true);
      const endpoint = studentId 
        ? `${API}/business-plan/ai/${studentId}`
        : `${API}/business-plan/ai/me`;
      
      const response = await axios.get(endpoint);
      
      if (response.data.has_plan) {
        setPlan({
          sections: response.data.sections,
          closing_statement: response.data.closing_statement
        });
        setRemainingRegenerations(response.data.remaining_regenerations || 0);
      } else {
        setPlan(null);
        setCanGenerate(response.data.can_generate || false);
        setCompletedSessions(response.data.completed_sessions || 0);
      }
    } catch (err) {
      console.error("Error loading AI plan:", err);
      if (err.response?.status === 404) {
        setPlan(null);
        setCanGenerate(false);
      } else {
        setError(err.response?.data?.detail || "Failed to load AI business plan");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (generating) return;
    
    setGenerating(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/business-plan/ai/generate`);
      
      if (response.data.success) {
        setPlan(response.data.plan);
        setRemainingRegenerations(response.data.remaining_regenerations);
        trackBusinessPlanCompleted({ role: user?.role });
      }
    } catch (err) {
      console.error("Error generating AI plan:", err);
      setError(err.response?.data?.detail || "Failed to generate AI business plan. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-16 h-16 text-purple-500 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading AI Business Plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 text-gray-600 hover:text-gray-800 font-semibold flex items-center gap-2"
          data-testid="back-btn"
        >
          ← Back
        </button>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Educator viewing note */}
        {studentId && user?.role !== "student" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-sm">
              <strong>Educator View:</strong> You are viewing this student&apos;s AI-generated business plan (read-only).
            </p>
          </div>
        )}

        {/* No Plan Yet - Generate CTA */}
        {!plan && !studentId && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                <Sparkles className="w-12 h-12 text-purple-500" />
              </div>
              
              <h1 className="text-3xl font-bold text-gray-800 mb-4">
                AI-Powered Business Plan
              </h1>
              
              {canGenerate ? (
                <>
                  <p className="text-gray-600 mb-6 max-w-lg mx-auto">
                    Congratulations on completing all 7 sessions! 🎉 LemonBot can now generate 
                    a polished, professional business plan by synthesizing all your reflections 
                    and ideas from the program.
                  </p>
                  
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                    <p className="text-purple-800 text-sm">
                      <strong>Note:</strong> You can regenerate your plan up to 3 times. 
                      The AI will synthesize your answers into clear, professional language 
                      without copying them word-for-word.
                    </p>
                  </div>
                  
                  <button
                    onClick={handleGenerate}
                    disabled={generating}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl font-bold text-lg hover:from-purple-600 hover:to-indigo-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
                    data-testid="generate-ai-plan-btn"
                  >
                    {generating ? (
                      <>
                        <Sparkles className="w-6 h-6 animate-spin" />
                        Generating Your Business Plan...
                      </>
                    ) : (
                      <>
                        <Rocket className="w-6 h-6" />
                        Generate My AI Business Plan
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-6">
                    Complete all 7 sessions to unlock your AI-generated business plan!
                  </p>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <p className="text-yellow-800">
                      You&apos;ve completed <strong>{completedSessions} of 7</strong> sessions. 
                      Keep going!
                    </p>
                  </div>
                  
                  <button
                    onClick={() => navigate("/dashboard")}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600 transition-colors"
                  >
                    Continue Learning
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* No Plan for Educator View */}
        {!plan && studentId && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="text-6xl mb-4">📋</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">No AI Plan Yet</h2>
              <p className="text-gray-600">
                This student hasn&apos;t generated an AI business plan yet.
              </p>
            </div>
          </div>
        )}

        {/* AI Business Plan Component */}
        {plan && (
          <AIBusinessPlan 
            plan={plan}
            studentName={user?.name || "Student"}
            onRegenerate={handleGenerate}
            canRegenerate={!studentId && user?.role === "student"}
            remainingRegenerations={remainingRegenerations}
            isGenerating={generating}
            onClose={() => navigate(-1)}
            showActions={!studentId || user?.role === "student"}
            canShare={!studentId && user?.role === "student"}
            hasDiscAssessment={user?.has_disc_assessment || false}
          />
        )}
      </div>
    </div>
  );
}

export default AIBusinessPlanPage;
